/** Mirrors CollectionCallOutcome on the server. The labels come from the API, not from here. */
export enum CollectionCallOutcome {
  PromiseToPay = 'promiseToPay',
  PaymentMade = 'paymentMade',
  PaymentPending = 'paymentPending',
  NoAnswer = 'noAnswer',
  Voicemail = 'voicemail',
  WrongNumber = 'wrongNumber',
  CallbackRequested = 'callbackRequested',
  BorrowerDispute = 'borrowerDispute',
  FinancialHardship = 'financialHardship',
  SupervisorEscalation = 'supervisorEscalation',
  Other = 'other',
}

export enum PromiseToPayStatus {
  Open = 'open',
  Kept = 'kept',
  Broken = 'broken',
  Cancelled = 'cancelled',
}

export interface ICollectionCall {
  id: number
  loanNumber: number
  byteproFileName?: number
  displayNo?: string
  borrowerName?: string
  /** The entity the loan is lent to. Blank on a loan borrowed by a person. */
  entityTitle?: string
  /** Language the primary borrower should be called in, blank when nobody recorded one. */
  preferredLanguage?: string
  daysPastDue?: number
  callDate: string
  callTime: string
  direction: string
  contactName: string
  contactPhone: string
  outcome: CollectionCallOutcome
  notes: string
  callbackDate: string
  ptpDate: string
  ptpAmount: number | null
  ptpStatus: PromiseToPayStatus | null
  /**
   * Email of whoever last moved the status by hand. Non-empty means the nightly sweep will never
   * judge this promise again: a person's ruling outranks it, deliberately. The screen has to say
   * so, or a collector who nudged a status has no way to know the row left automation.
   */
  ptpStatusSetBy: string
  ptpResolvedDate: string
  ptpPaidAmount: number | null
  employee: string
  createdBy: string
  createdAt: string
  /** Set when the call was placed from the app: what the carrier reported about the dial. */
  phoneCallId?: number | null
  durationSec?: number | null
  phoneCallStatus?: string | null
  hasRecording?: boolean
}

/** Fallback labels, used only until the API's list lands (and for rendering historical rows). */
export const outcomeLabels: Record<string, string> = {
  [CollectionCallOutcome.PromiseToPay]: 'Promise to Pay (PTP)',
  [CollectionCallOutcome.PaymentMade]: 'Payment Made',
  [CollectionCallOutcome.PaymentPending]: 'Payment Pending',
  [CollectionCallOutcome.NoAnswer]: 'No Answer',
  [CollectionCallOutcome.Voicemail]: 'Voicemail',
  [CollectionCallOutcome.WrongNumber]: 'Wrong Number',
  [CollectionCallOutcome.CallbackRequested]: 'Callback Requested',
  [CollectionCallOutcome.BorrowerDispute]: 'Borrower Dispute',
  [CollectionCallOutcome.FinancialHardship]: 'Financial Hardship / Unable to Pay',
  [CollectionCallOutcome.SupervisorEscalation]: 'Supervisor Escalation',
  [CollectionCallOutcome.Other]: 'Other',
}

export const ptpStatusLabels: Record<string, string> = {
  [PromiseToPayStatus.Open]: 'Open',
  [PromiseToPayStatus.Kept]: 'Kept',
  [PromiseToPayStatus.Broken]: 'Broken',
  [PromiseToPayStatus.Cancelled]: 'Cancelled',
}

/** Badge colours for a promise, so a broken one is visible without reading the column. */
export const ptpStatusClass: Record<string, string> = {
  [PromiseToPayStatus.Open]: 'bg-blue-100 text-blue-700',
  [PromiseToPayStatus.Kept]: 'bg-green-100 text-green-700',
  [PromiseToPayStatus.Broken]: 'bg-red-100 text-red-700',
  [PromiseToPayStatus.Cancelled]: 'bg-gray-100 text-gray-600',
}

/** Outcomes that carry their own follow-up, highlighted in the grid. */
export const outcomeClass: Record<string, string> = {
  [CollectionCallOutcome.PromiseToPay]: 'bg-indigo-100 text-indigo-700',
  [CollectionCallOutcome.PaymentMade]: 'bg-green-100 text-green-700',
  [CollectionCallOutcome.PaymentPending]: 'bg-teal-100 text-teal-700',
  [CollectionCallOutcome.BorrowerDispute]: 'bg-amber-100 text-amber-700',
  [CollectionCallOutcome.FinancialHardship]: 'bg-amber-100 text-amber-700',
  [CollectionCallOutcome.SupervisorEscalation]: 'bg-red-100 text-red-700',
}

/** Mirrors PhoneCallStatus on the server. */
export enum PhoneCallStatus {
  Queued = 'queued',
  Ringing = 'ringing',
  InProgress = 'inProgress',
  Completed = 'completed',
  NoAnswer = 'noAnswer',
  Busy = 'busy',
  Failed = 'failed',
  Canceled = 'canceled',
}

/** Written from the collector's point of view: the first phone to ring is their own. */
export const phoneCallStatusLabels: Record<string, string> = {
  [PhoneCallStatus.Queued]: 'Dialing',
  [PhoneCallStatus.Ringing]: 'Ringing your phone',
  [PhoneCallStatus.InProgress]: 'Connected',
  [PhoneCallStatus.Completed]: 'Call ended',
  [PhoneCallStatus.NoAnswer]: 'No answer',
  [PhoneCallStatus.Busy]: 'Busy',
  [PhoneCallStatus.Failed]: 'Call failed',
  [PhoneCallStatus.Canceled]: 'Call canceled',
}

/** The call is over, however it ended. */
export const PHONE_CALL_FINAL_STATUSES: string[] = [
  PhoneCallStatus.Completed,
  PhoneCallStatus.NoAnswer,
  PhoneCallStatus.Busy,
  PhoneCallStatus.Failed,
  PhoneCallStatus.Canceled,
]

export interface IPhoneCall {
  id: number
  loanNumber: number
  status: PhoneCallStatus
  toNumber: string
  agentPhone: string
  fromNumber: string
  contactName: string
  agentName: string
  agentEmail: string
  durationSec: number
  recordingStatus: string
  recordingDurationSec: number
  hasRecording?: boolean
  errorMessage: string
  collectionCallId: number | null
  createdAt: string
}

/** Mirrors CollectionQueueStatus on the server. */
export enum CollectionQueueStatus {
  Open = 'open',
  Working = 'working',
  Snoozed = 'snoozed',
  Resolved = 'resolved',
}

export const queueStatusLabels: Record<string, string> = {
  [CollectionQueueStatus.Open]: 'Open',
  [CollectionQueueStatus.Working]: 'Working',
  [CollectionQueueStatus.Snoozed]: 'Snoozed',
  [CollectionQueueStatus.Resolved]: 'Resolved',
}

export const queueStatusClass: Record<string, string> = {
  [CollectionQueueStatus.Open]: 'bg-blue-100 text-blue-700',
  [CollectionQueueStatus.Working]: 'bg-indigo-100 text-indigo-700',
  [CollectionQueueStatus.Snoozed]: 'bg-gray-100 text-gray-600',
  [CollectionQueueStatus.Resolved]: 'bg-green-100 text-green-700',
}

export interface ICollectionQueueItem {
  id: number
  loanNumber: number
  byteproFileName?: number
  entityTitle?: string
  borrowerName?: string
  primaryPhone?: string
  /** The loan's tags, so a collector sees the legal state before dialling and not after. */
  tags?: string[]
  status: CollectionQueueStatus
  source: string
  priority: number
  daysPastDue: number
  /**
   * 1 when the installment the loan is behind on is its first scheduled payment, 2 the second,
   * 0 for everything later. The two are worked ahead of the rest of the list.
   */
  paymentSequence?: number
  unpaidBalance: number | null
  reason: string
  assignedTo: string
  assignedToName: string
  assignedGroupId: number | null
  assignedGroupName: string
  nextActionDate: string
  snoozedUntil: string
  snoozeReason: string
  lastCallDate: string
  lastOutcome: string
  lastCallBy: string
  callCount: number
  notes: string
  resolvedDate: string
  resolvedReason: string
}
